import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertPosMessageForm } from '../hooks/useUpsertPosMessageForm';
import type { UpsertPosMessageDto } from '../types/PosMessageDto';
import { UpsertPosMessageForm } from './UpsertPosMessageForm';
import { Title } from '../../../components/Title';

interface NewPosMessageModalProps {
	close: () => void;
	opened: boolean;
	triggerListUpdate: () => void;
}

export function NewPosMessageModal({
	opened,
	close,
	triggerListUpdate,
}: NewPosMessageModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertPosMessageForm();

	const newPosMessageMutation = mutate<UpsertPosMessageDto>(
		`${organisation}/pos-messages/new`,
		inputs,
		'multipart/form-data',
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		triggerListUpdate();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={resetAndClose}
				title={<Title label={'New POS Message'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newPosMessageMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'POS Message Added!'}
					body={
						'A new POS message has been successfully added. Click Ok to continue...'
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newPosMessageMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertPosMessageForm form={form} inputs={inputs} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
